import React, { useEffect, useState } from "react";

/**
 * Cross-fading hero slideshow with a subtle Ken-Burns zoom on every slide.
 *
 * - Pure CSS animation (no extra deps), respects prefers-reduced-motion.
 * - All images are absolutely stacked; only the active one is opacity:1.
 * - Each slide gently zooms from scale(1) → scale(1.08) while it's visible.
 */
export default function HeroSlideshow({ images, interval = 6000 }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!images || images.length < 2) return;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % images.length),
      interval
    );
    return () => clearInterval(id);
  }, [images, interval]);

  if (!images || images.length === 0) return null;

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      data-testid="hero-slideshow"
    >
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          aria-hidden="true"
          data-testid={`hero-slide-${i}`}
          className={
            "absolute inset-0 w-full h-full object-cover " +
            "transition-opacity duration-[1400ms] ease-in-out " +
            (i === index ? "opacity-100" : "opacity-0")
          }
          style={{
            animation:
              i === index
                ? `heroKenBurns ${Math.round(interval * 1.6) / 1000}s ease-out forwards`
                : "none",
          }}
        />
      ))}
      {/* Ken Burns keyframes — injected once via a <style> tag */}
      <style>{`
        @keyframes heroKenBurns {
          0%   { transform: scale(1)    translate3d(0,0,0); }
          100% { transform: scale(1.08) translate3d(-1%,-1%,0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="hero-slideshow"] img { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
